import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Maximize2, X } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  PRESENTATION_PANEL_WIDTH,
} from '../../../theme';
import { DockedPanel } from '../../layout/DockedPanel';
import { CURSOR_POINTER } from '../../../utils/webStyles';
import { SlideStage } from './SlideStage';
import { SlideshowControls } from './SlideshowControls';
import { focusMapOnSlide } from './focusMapOnSlide';
import type { SlideshowController } from './useSlideshowController';

// Offer row height plus its gap to the stage; reserved even when empty.
const UPDATE_SLOT_HEIGHT = 56;

/** What the panel draws from the controller and the view watch. MapSlideshow
 *  keeps the last open snapshot of it, so the panel shows what it had while
 *  it slides out instead of the controller's cleared state. */
export interface PresentationPanelContent {
  current: SlideshowController['current'];
  photoIndex: number | null;
  photoCount: number;
  autoplay: boolean;
  /** Photo slides a deck built from where the map is now would hold. */
  inViewCount: number;
  /** The map has moved off the presented deck's bounds. */
  viewMoved: boolean;
}

/**
 * Desktop-shell surface: the presentation docked beside the map, on the same
 * right-hand panel a selected structure uses. The map stays live behind it,
 * so the address line can center the map without closing anything.
 *
 * Because the map stays live, the presenter can pan or zoom away from the
 * area the deck was built from. The deck does not follow: it updates on
 * request. When the view has moved, an offer row appears under the header
 * saying how many observations the new view holds, with an Update button
 * that rebuilds the deck. Move back onto the presented area and the row
 * goes away on its own, having changed nothing.
 */
export function PresentationPanel({
  controller,
  content,
  shown = true,
  onExited,
}: {
  /** Live callbacks only; everything drawn comes from `content`. */
  controller: SlideshowController;
  content: PresentationPanelContent;
  /** False while the exit plays: the panel is inert and slides out. */
  shown?: boolean;
  onExited?: () => void;
}) {
  const { colors } = useTheme();
  const { current, photoIndex, photoCount, autoplay, inViewCount, viewMoved } = content;
  const canUpdate = inViewCount > 0;

  return (
    <DockedPanel
      testID="map-slideshow-panel"
      width={PRESENTATION_PANEL_WIDTH}
      shown={shown}
      onExited={onExited}>
      {/* DockedPanel adds no padding of its own (StructureDetailSheet
          supplies its own throughout) — this content view carries it. */}
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={[styles.headerLabel, { color: colors.textSecondary }]}>Presentation</Text>
          <Pressable
            onPress={controller.close}
            testID="map-slideshow-close"
            accessibilityRole="button"
            accessibilityLabel="Close presentation"
            style={[styles.headerClose, CURSOR_POINTER]}>
            <X size={18} color={colors.textSecondary} />
          </Pressable>
        </View>

        {/* The slot is always laid out at the row's height so the stage below
            never moves when the offer appears or withdraws. */}
        <View style={styles.updateSlot}>
          {viewMoved ? (
            <View
              testID="map-slideshow-update-row"
              style={[styles.updateRow, { borderColor: colors.border }]}>
              <Text style={[styles.updateText, { color: colors.textSecondary }]} numberOfLines={1}>
                {canUpdate
                  ? `${inViewCount} observation${inViewCount === 1 ? '' : 's'} in this view`
                  : 'No observations in this view'}
              </Text>
              <Pressable
                onPress={controller.rebuildDeck}
                disabled={!canUpdate}
                testID="map-slideshow-update"
                accessibilityRole="button"
                accessibilityLabel="Update the presentation to this view"
                aria-disabled={!canUpdate}
                style={[
                  styles.updateButton,
                  { borderColor: canUpdate ? colors.borderStrong : colors.border },
                  canUpdate ? CURSOR_POINTER : null,
                ]}>
                <Text
                  style={[
                    styles.updateButtonLabel,
                    { color: canUpdate ? colors.text : colors.textTertiary },
                  ]}>
                  Update
                </Text>
              </Pressable>
            </View>
          ) : null}
        </View>

        <SlideStage
          layout="panel"
          item={current}
          counterText={photoIndex ? `${photoIndex} of ${photoCount}` : undefined}
          onPrev={controller.prev}
          onNext={controller.next}
          onAddressPress={focusMapOnSlide}
        />

        {current ? (
          <SlideshowControls
            autoplay={autoplay}
            onPrev={controller.prev}
            onNext={controller.next}
            onToggleAutoplay={controller.toggleAutoplay}
            iconColor={colors.text}
            style={styles.controls}
          />
        ) : null}

        <Pressable
          onPress={controller.enterFullscreen}
          testID="map-slideshow-fullscreen"
          accessibilityRole="button"
          accessibilityLabel="Open full screen presentation"
          style={[styles.fullscreenButton, { backgroundColor: colors.action }, CURSOR_POINTER]}>
          <Maximize2 size={16} color={colors.actionOn} />
          <Text style={[styles.fullscreenLabel, { color: colors.actionOn }]}>Full screen</Text>
        </Pressable>
      </View>
    </DockedPanel>
  );
}

// The offer row's box, drawn whether or not it is on screen, so the two
// states differ by presence alone and never by size.
const UPDATE_ROW_HEIGHT = 44;

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: SPACING.md,
  },
  headerLabel: {
    ...TYPOGRAPHY.overline,
  },
  headerClose: {
    padding: SPACING.xxs,
  },
  // Fixed height: the row appears and disappears as the presenter moves the
  // map, and the stage under it must never resize when it does.
  updateSlot: {
    height: UPDATE_SLOT_HEIGHT,
    justifyContent: 'flex-end',
  },
  updateRow: {
    height: UPDATE_ROW_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING.sm,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    paddingLeft: SPACING.smd,
    paddingRight: SPACING.xs,
    marginBottom: SPACING.smd,
  },
  updateText: {
    ...TYPOGRAPHY.metadata,
    flexShrink: 1,
  },
  updateButton: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
  },
  updateButtonLabel: {
    ...TYPOGRAPHY.labelSmall,
  },
  controls: {
    paddingVertical: SPACING.smd,
  },
  fullscreenButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.xs,
    borderRadius: BORDER_RADIUS.md,
    paddingVertical: SPACING.sm,
  },
  fullscreenLabel: {
    ...TYPOGRAPHY.button,
  },
});
